import { queryKeys } from '@/lib/query-keys';
import { addressClient } from '@/lib/api/ts-rest';
import { createLogger } from '@/lib/logger';
import type {
  CreateAddressBody,
  UpdateAddressBody,
} from '@octave/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const log = createLogger('useAddressActions');

export const useCreateAddress = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, CreateAddressBody>({
    mutationFn: async (address) => {
      const res = await addressClient.createAddress({ body: address });
      if (res.status !== 201) throw new Error('Failed to create address');
      return res.body;
    },
    onError: (error) => {
      log.error('Error creating address', error);
      toast.error("Erreur lors de la création de l'adresse");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.addresses.list() });
    },
  });
};

export const useUpdateAddress = (id: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, UpdateAddressBody>({
    mutationFn: async (address) => {
      const res = await addressClient.updateAddress({
        params: { id: id! },
        body: address,
      });
      if (res.status !== 200) throw new Error('Failed to update address');
      return res.body;
    },
    onError: (error) => {
      log.error('Error updating address', error);
      toast.error("Erreur lors de la mise à jour de l'adresse");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.addresses.list() });
    },
  });
};

export const useDeleteAddress = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, string>({
    mutationFn: async (id) => {
      const res = await addressClient.deleteAddress({ params: { id } });
      if (res.status !== 200) throw new Error('Failed to delete address');
      return res.body;
    },
    onError: (error) => {
      log.error('Error deleting address', error);
      toast.error("Erreur lors de la suppression de l'adresse");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.addresses.list() });
    },
  });
};
